import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { User } from '../types';
import { listenForSignout } from '../lib/tabCommunication';
import { identifyUser, resetUser, analytics } from '../lib/analytics';

interface AuthState {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  userProfile: {
    firstName: string | null;
    lastName: string | null;
    secondLastName: string | null;
  } | null;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, firstName: string, lastName1: string, lastName2?: string, skipEmailConfirmation?: boolean, policiesAcceptedAt?: string, sensitiveDataConsentAt?: string) => Promise<{ success: boolean; error?: string }>;
  signUpViaInvitation: (token: string, email: string, password: string, firstName: string, lastName1: string, lastName2?: string, policiesAcceptedAt?: string, sensitiveDataConsentAt?: string) => Promise<{ success: boolean; error?: string; requiresLogin?: boolean }>;
  signInWithGoogle: (code: string, policiesAcceptedAt?: string, sensitiveDataConsentAt?: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  signOutLocal: () => Promise<void>;
  initialize: () => Promise<void>;
  cleanup: () => void;
  updateUserBookInterest: (interested: boolean) => Promise<void>;
  loadUserProfile: () => Promise<void>;
  persistUserProfile: (firstName: string, lastName: string, secondLastName?: string | null) => void;
  isDevMode: boolean;
  toggleDevMode: () => void;
}

// Global auth subscription to prevent duplicates
let authSubscription: { unsubscribe: () => void } | null = null;

// Heartbeat interval to keep session alive
let heartbeatInterval: NodeJS.Timeout | null = null;

// Heartbeat functions to maintain session
const startHeartbeat = () => {
  // Clear any existing interval
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }

  // Ping every 25 minutes (before 1-hour token expiry)
  heartbeatInterval = setInterval(async () => {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error('[AuthStore] Heartbeat session check failed:', error);

        // Attempt manual refresh
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('[AuthStore] Session refresh failed:', refreshError);
        }
      } else if (data.session) {
        // Check token expiry time
        const expiresAt = data.session.expires_at || 0;
        const now = Math.floor(Date.now() / 1000);
        const timeRemaining = expiresAt - now;

        // Force refresh if less than 5 minutes remaining
        if (timeRemaining < 300) {
          console.warn('[AuthStore] Token expiring soon, forcing refresh');
          await supabase.auth.refreshSession();
        }
      }
    } catch (err) {
      console.error('[AuthStore] Heartbeat error:', err);
    }
  }, 25 * 60 * 1000); // 25 minutes

  console.log('[AuthStore] Heartbeat started (25 min interval)');
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
    console.log('[AuthStore] Heartbeat stopped');
  }
};

/**
 * Translate common Supabase auth error messages to Spanish.
 */
const translateAuthError = (message: string): string => {
  const translations: Record<string, string> = {
    'Invalid login credentials': 'Email o contraseña incorrectos',
    'Email not confirmed': 'Tu email aún no está confirmado. Revisa tu bandeja de entrada y haz clic en el enlace de confirmación.',
    'Invalid email or password': 'Email o contraseña incorrectos',
    'User already registered': 'Este email ya está registrado. Intenta iniciar sesión.',
    'Password should be at least 6 characters': 'La contraseña debe tener al menos 6 caracteres',
    'Signup requires a valid password': 'Se requiere una contraseña válida',
    'Unable to validate email address: invalid format': 'El formato del email no es válido',
    'For security purposes, you can only request this after': 'Por seguridad, debes esperar antes de volver a intentarlo.',
    'Email rate limit exceeded': 'Demasiados intentos. Por favor, espera unos minutos.',
    'User not found': 'No se encontró una cuenta con ese email',
    'New password should be different from the old password': 'La nueva contraseña debe ser diferente a la anterior',
    'Auth session missing': 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.',
  };

  for (const [english, spanish] of Object.entries(translations)) {
    if (message.includes(english)) return spanish;
  }

  // Partial matches for messages with dynamic content
  if (message.includes('after') && message.includes('seconds')) {
    const seconds = message.match(/(\d+)\s*seconds/);
    return seconds
      ? `Por seguridad, debes esperar ${seconds[1]} segundos antes de volver a intentarlo.`
      : 'Por seguridad, debes esperar antes de volver a intentarlo.';
  }

  return message;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  isAdmin: false,
  isDevMode: localStorage.getItem('ethernal_dev_mode') === 'true',
  toggleDevMode: () => {
    const current = get().isDevMode;
    const next = !current;
    localStorage.setItem('ethernal_dev_mode', String(next));
    set({ isDevMode: next });
  },
  userProfile: null,

  signIn: async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { success: false, error: translateAuthError(error.message) };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Error de conexión' };
    }
  },

  signUp: async (
    email: string,
    password: string,
    firstName: string,
    lastName1: string,
    lastName2?: string,
    skipEmailConfirmation: boolean = false,
    policiesAcceptedAt?: string,
    sensitiveDataConsentAt?: string
  ) => {
    try {
      const signUpOptions: any = {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: {
          first_name: firstName.trim(),
          last_name_1: lastName1.trim(),
          last_name_2: (lastName2 || '').trim()
        }
      };

      if (skipEmailConfirmation) {
        signUpOptions.data.email_confirm = true;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: signUpOptions
      });

      if (error) {
        return { success: false, error: translateAuthError(error.message) };
      }

      // Upsert user profile after signup (trigger may have already created it)
      if (data.user) {
        const profileRow: Record<string, any> = {
          id: data.user.id,
          email: email.toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName1.trim(),
          second_last_name: lastName2 ? lastName2.trim() : null,
          full_name: `${firstName.trim()} ${lastName1.trim()}${lastName2 ? ' ' + lastName2.trim() : ''}`.trim()
        };
        if (policiesAcceptedAt) profileRow.policies_accepted_at = policiesAcceptedAt;
        if (sensitiveDataConsentAt) profileRow.sensitive_data_consent_at = sensitiveDataConsentAt;

        const { error: profileError } = await supabase
          .from('user_profiles')
          .upsert(profileRow, { onConflict: 'id' });

        if (profileError) {
          console.error('[AuthStore] Error upserting user profile:', profileError);
          // Continue anyway, profile should exist from trigger
        }

        analytics.signupCompleted(data.user.id, 'email');
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Error de conexión' };
    }
  },

  signUpViaInvitation: async (
    token: string,
    email: string,
    password: string,
    firstName: string,
    lastName1: string,
    lastName2?: string,
    policiesAcceptedAt?: string,
    sensitiveDataConsentAt?: string
  ) => {
    try {
      console.log('[AuthStore] signUpViaInvitation called for:', email);

      // Call the Edge Function to create user without email confirmation
      const functionUrl = `${supabase.supabaseUrl}/functions/v1/accept-invitation-signup`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabase.supabaseKey}`
        },
        body: JSON.stringify({
          token,
          email,
          password,
          firstName,
          lastName1,
          lastName2: lastName2 || '',
          policiesAcceptedAt,
          sensitiveDataConsentAt
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[AuthStore] Edge Function error:', data);

        // Handle specific error cases
        if (data.error === 'INVALID_TOKEN') {
          return { success: false, error: 'Token de invitación no válido' };
        } else if (data.error === 'EXPIRED') {
          return { success: false, error: 'Esta invitación ha expirado' };
        } else if (data.error === 'ALREADY_ACCEPTED') {
          return { success: false, error: 'Esta invitación ya ha sido usada' };
        } else if (data.error === 'EMAIL_MISMATCH') {
          return { success: false, error: `Esta invitación fue enviada a ${data.expectedEmail}` };
        } else if (data.error === 'USER_EXISTS') {
          return { success: false, error: 'Este email ya tiene una cuenta. Por favor, inicia sesión.' };
        } else if (data.error === 'CONSENT_REQUIRED') {
          return { success: false, error: data.message || 'Debes aceptar los términos y el consentimiento para continuar' };
        }

        return { success: false, error: data.message || 'Error al crear la cuenta' };
      }

      console.log('[AuthStore] ✅ User created via Edge Function, now logging in...');

      // Signal BEFORE signIn — signIn triggers SIGNED_IN event which fires
      // processInvitation useEffect. The flag must exist before that happens.
      if (data.familyGroupId) {
        sessionStorage.setItem('invitation_completed_by_edge_function', data.familyGroupId);
      }

      // User created successfully, now sign in
      const signInResult = await get().signIn(email, password);

      if (!signInResult.success) {
        console.error('[AuthStore] Auto-login failed:', signInResult.error);
        sessionStorage.removeItem('invitation_completed_by_edge_function');
        // User was created but couldn't auto-login, ask them to login manually
        return {
          success: true,
          requiresLogin: true,
          error: 'Cuenta creada. Por favor, inicia sesión para continuar.'
        };
      }

      console.log('[AuthStore] ✅ User logged in successfully');
      analytics.signupCompleted(data.userId || '', 'invitation');

      return { success: true };

    } catch (error: any) {
      console.error('[AuthStore] signUpViaInvitation error:', error);
      return { success: false, error: error.message || 'Error de conexión' };
    }
  },

  signInWithGoogle: async (
    code: string,
    policiesAcceptedAt?: string,
    sensitiveDataConsentAt?: string
  ) => {
    try {
      // Exchange auth code for id_token via our serverless function
      const exchangeResponse = await fetch('/api/auth/google-exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      const exchangeData = await exchangeResponse.json();

      if (!exchangeResponse.ok || !exchangeData.id_token) {
        return { success: false, error: exchangeData.error || 'Error al verificar con Google' };
      }

      const { data: signInData, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: exchangeData.id_token,
      });

      if (error) {
        return { success: false, error: translateAuthError(error.message) };
      }

      // If consent timestamps were provided (Google OAuth at signup, not login),
      // record them on user_profiles. For existing users logging in with Google,
      // these will be undefined and we skip this block.
      //
      // The on_auth_user_created trigger creates the user_profiles row asynchronously
      // after signInWithIdToken returns. We use UPDATE (not upsert) with a bounded
      // retry loop so we don't insert a partial row that violates NOT NULL constraints
      // on email/first_name/etc. while the trigger is still running.
      if (signInData?.user && (policiesAcceptedAt || sensitiveDataConsentAt)) {
        const consentUpdate: Record<string, any> = {};
        if (policiesAcceptedAt) consentUpdate.policies_accepted_at = policiesAcceptedAt;
        if (sensitiveDataConsentAt) consentUpdate.sensitive_data_consent_at = sensitiveDataConsentAt;

        let updated = false;
        for (let attempt = 0; attempt < 5 && !updated; attempt++) {
          const { data, error: consentError } = await supabase
            .from('user_profiles')
            .update(consentUpdate)
            .eq('id', signInData.user.id)
            .select('id');

          if (consentError) {
            console.error('[AuthStore] Error recording Google-signup consent:', consentError);
            break;
          }
          if (data && data.length > 0) {
            updated = true;
            break;
          }
          // Row not yet created by trigger; wait 200ms and retry (max ~1s total).
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        if (!updated) {
          console.warn('[AuthStore] Google-signup consent could not be recorded — user_profiles row did not appear after retries');
        }
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Error de conexión con Google' };
    }
  },

  resetPassword: async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        return { success: false, error: translateAuthError(error.message) };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Error al enviar el correo' };
    }
  },

  updatePassword: async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        if (error.message.includes('session')) {
          return { success: false, error: 'Sesión expirada. Por favor, solicita un nuevo enlace de recuperación.' };
        }
        return { success: false, error: translateAuthError(error.message) };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Error de conexión. Por favor, intenta nuevamente.' };
    }
  },

  signOut: async () => {
    try {
      // Stop heartbeat
      stopHeartbeat();
      resetUser();

      // Limpiar localStorage
      localStorage.removeItem('activeFamilyId');
      localStorage.removeItem('userProfile');

      // Logout de Supabase
      await supabase.auth.signOut();

      // Limpiar state local
      set({ user: null, isAdmin: false, userProfile: null, loading: false });

      // Forzar navegación a home y recargar para limpiar todo el estado
      window.location.href = '/';
    } catch (error) {
      console.error('[AuthStore] Error during signOut:', error);
      // Forzar logout aunque haya error
      await supabase.auth.signOut();
      set({ user: null, isAdmin: false, userProfile: null, loading: false });
      window.location.href = '/';
    }
  },

  signOutLocal: async () => {
    try {
      console.log('[AuthStore] Executing local signout (triggered by other tab)');

      // Check if we're in password reset flow - don't interfere
      const isPasswordResetFlow = sessionStorage.getItem('password_reset_in_progress');
      if (isPasswordResetFlow === 'true' || window.location.pathname === '/reset-password') {
        console.log('[AuthStore] ⚠️ Ignoring signout - password reset in progress on this tab');
        return;
      }

      // Stop heartbeat
      stopHeartbeat();

      // Limpiar localStorage
      localStorage.removeItem('activeFamilyId');
      localStorage.removeItem('userProfile');

      // Logout de Supabase
      await supabase.auth.signOut();

      // Limpiar state local
      set({ user: null, isAdmin: false, userProfile: null, loading: false });

      // Navegar a home (sin reload para evitar loops)
      window.location.href = '/';
    } catch (error) {
      console.error('[AuthStore] Error during signOutLocal:', error);
      await supabase.auth.signOut();
      set({ user: null, isAdmin: false, userProfile: null, loading: false });
      window.location.href = '/';
    }
  },

  initialize: async () => {
    set({ loading: true });

    // Cleanup existing subscription first to prevent duplicates
    if (authSubscription) {
      console.log('[AuthStore] Cleaning up existing auth subscription');
      authSubscription.unsubscribe();
      authSubscription = null;
    }

    // Setup tab communication listener
    listenForSignout(() => {
      console.log('[AuthStore] Received force signout from another tab');
      get().signOutLocal();
    });

    // PRO-348 iter 11.3 — implicit-flow hash bridge for admin-generated
    // magic links. The Supabase JS client is configured with
    // `flowType: 'pkce'` (better security for browser apps), which only
    // watches for `?code=...` in the query string via
    // detectSessionInUrl. But `supabase.auth.admin.generateLink({ type:
    // 'magiclink' })` in our send-install-instructions Edge Function
    // generates an action_link that, after Supabase verifies the token,
    // redirects to /activate-push with `#access_token=...&refresh_
    // token=...` appended to the hash (implicit-grant style). The PKCE
    // client ignores hash fragments, so getSession() returned null and
    // ActivatePushPage showed the sign-in-prompt stage on a fresh,
    // never-used magic link.
    //
    // Parse the hash here, call setSession explicitly, then strip the
    // hash so a reload doesn't re-trigger. Other auth flows (Google
    // OAuth, password reset, invitation signup) don't produce
    // implicit-flow hashes, so this is a no-op for them.
    if (typeof window !== 'undefined' && window.location.hash) {
      const rawHash = window.location.hash.startsWith('#')
        ? window.location.hash.substring(1)
        : window.location.hash;
      const hashParams = new URLSearchParams(rawHash);
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      if (accessToken && refreshToken) {
        try {
          console.log('[AuthStore] Detected implicit-flow hash, bridging to setSession');
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (setSessionError) {
            console.warn(
              '[AuthStore] setSession from hash failed:',
              setSessionError.message,
            );
          }
          window.history.replaceState(
            {},
            '',
            window.location.pathname + window.location.search,
          );
        } catch (err) {
          console.warn('[AuthStore] hash bridge exception:', err);
        }
      }
    }

    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      const user = {
        id: session.user.id,
        email: session.user.email!,
        created_at: session.user.created_at
      };

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('is_admin')
        .eq('id', session.user.id)
        .maybeSingle();

      set({
        user,
        isAdmin: profile?.is_admin || false,
        loading: false
      });

      // Start heartbeat for this session
      startHeartbeat();

      // Identify user for analytics
      identifyUser(user.id, { email: user.email });

      get().loadUserProfile();
    } else {
      set({ user: null, isAdmin: false, userProfile: null, loading: false });
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthStore] Auth state change:', event, { hasSession: !!session });

      // Ignore INITIAL_SESSION to prevent duplicate processing
      if (event === 'INITIAL_SESSION') {
        console.log('[AuthStore] Ignoring INITIAL_SESSION - already handled in initialize');
        return;
      }

      // Ignore TOKEN_REFRESHED if user hasn't changed
      if (event === 'TOKEN_REFRESHED') {
        const currentUser = get().user;
        if (currentUser?.id === session?.user?.id) {
          console.log('[AuthStore] TOKEN_REFRESHED - no state change needed (same user)');
          return;
        }
      }

      // Handle password recovery flow
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[AuthStore] Password recovery event detected');
        // Session is already set by Supabase, just update our state
        if (session?.user) {
          const user = {
            id: session.user.id,
            email: session.user.email!,
            created_at: session.user.created_at
          };

          const { data: profile } = await supabase
            .from('user_profiles')
            .select('is_admin')
            .eq('id', session.user.id)
            .maybeSingle();

          set({
            user,
            isAdmin: profile?.is_admin || false,
            loading: false
          });
        }
        return;
      }

      // Handle USER_UPDATED event - ignore it during password reset flow
      if (event === 'USER_UPDATED') {
        console.log('[AuthStore] USER_UPDATED event detected');

        // Check if we're in password reset flow
        const isPasswordResetFlow = sessionStorage.getItem('password_reset_in_progress');
        if (isPasswordResetFlow === 'true') {
          console.log('[AuthStore] Ignoring USER_UPDATED during password reset flow');
          return;
        }

        // For normal USER_UPDATED events (like profile updates), just log it
        // Don't update state to avoid unnecessary re-renders and cleanups
        console.log('[AuthStore] USER_UPDATED event - no action needed (session unchanged)');
        return;
      }

      // Handle SIGNED_IN event - check if user actually changed to prevent unnecessary re-renders
      if (event === 'SIGNED_IN') {
        const currentUser = get().user;
        if (currentUser?.id === session?.user?.id) {
          console.log('[AuthStore] SIGNED_IN - no state change needed (same user already signed in)');
          return;
        }
        // New sign in - start heartbeat
        startHeartbeat();
      }

      // Handle SIGNED_OUT event - stop heartbeat
      if (event === 'SIGNED_OUT') {
        stopHeartbeat();
      }

      // Handle other events (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, etc.)
      if (session?.user) {
        const user = {
          id: session.user.id,
          email: session.user.email!,
          created_at: session.user.created_at
        };

        const { data: profile } = await supabase
          .from('user_profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .maybeSingle();

        set({
          user,
          isAdmin: profile?.is_admin || false,
          loading: false
        });
      } else {
        set({ user: null, isAdmin: false, loading: false });
      }
    });

    // Store subscription reference for cleanup
    authSubscription = subscription;
    console.log('[AuthStore] Auth subscription established');
  },

  cleanup: () => {
    console.log('[AuthStore] Cleanup called');

    // Stop heartbeat
    stopHeartbeat();

    if (authSubscription) {
      console.log('[AuthStore] Unsubscribing from auth changes');
      authSubscription.unsubscribe();
      authSubscription = null;
    }
  },

  updateUserBookInterest: async (interested: boolean) => {
    const { user } = get();
    if (!user) return;

    try {
      await supabase
        .from('user_metrics')
        .upsert({
          user_id: user.id,
          email: user.email,
          expressed_book_interest: interested
        });
    } catch (error) {
      console.error('Error updating book interest:', error);
    }
  },

  loadUserProfile: async () => {
    try {
      const storedProfile = localStorage.getItem('userProfile');
      if (storedProfile) {
        const profile = JSON.parse(storedProfile);
        set({ userProfile: profile });
        console.log('[AuthStore] Loaded user profile from localStorage:', profile);
        return;
      }

      const { user } = get();
      if (!user) return;

      const { data } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, second_last_name')
        .eq('id', user.id)
        .maybeSingle();

      if (data) {
        const profile = {
          firstName: data.first_name,
          lastName: data.last_name,
          secondLastName: data.second_last_name,
        };
        set({ userProfile: profile });
        localStorage.setItem('userProfile', JSON.stringify(profile));
        console.log('[AuthStore] Loaded and persisted user profile:', profile);
      }
    } catch (error) {
      console.error('[AuthStore] Error loading user profile:', error);
    }
  },

  persistUserProfile: (firstName: string, lastName: string, secondLastName?: string | null) => {
    const profile = {
      firstName,
      lastName,
      secondLastName: secondLastName || null,
    };
    set({ userProfile: profile });
    localStorage.setItem('userProfile', JSON.stringify(profile));
    console.log('[AuthStore] Persisted user profile:', profile);
  },
}));